import { Component, OnInit, ChangeDetectorRef, DestroyRef, inject } from '@angular/core';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { filter, noop } from 'rxjs';

import { clone, isEmpty, zip } from 'lodash-es';
import moment from 'moment';
import { Store } from '@ngxs/store';
import { UserState } from '@state/user/state';
import { AccountState } from '@state/account/state';
import { AccountService } from 'app/core';
import { RelatedToType } from '@shared/enums';
import { FieldDef, SavedView } from '@core/models';
import { FeatureFlag, NewPermission } from '@core/enums';
import { PropertyTenantsService } from '../tenants.service';
import { Property } from 'app/modules/projects/projects.types';
import { PanelDef } from '@shared/components/panels/panels.types';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { PropertyTenantsAddUnitComponent } from '../modals/add-unit/add-unit.component';
import { PropertyUnit, PropertyTenant } from 'app/modules/projects/details/tenants/tenants.types';
@Component({
  selector: 'property-tenant-requests',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
})
export class PropertyTenantsListComponent implements OnInit {
  data: Data;
  isLayoutChange: boolean = false;
  propertyUnits: PropertyUnit[];
  selectedPropertyUnit: PropertyUnit;
  panelDef: PanelDef<any>;
  fields: any[] = [];
  fieldsCombined: any[] = [];
  editMode: boolean;
  titleFieldDef: any;
  NewPermission = NewPermission;

  private property: Property;
  private savedViews: SavedView[];
  private savedView: SavedView;
  private fieldDefs: FieldDef[] = [];
  private panels: any[];
  private dashboardDef: any;
  private originalFields: any[] = [];
  private defaultFieldDefs: FieldDef[] = [];
  private panelName: string;
  private _selectedPropertyUnitId: number | null = null;

  get selectedPropertyUnitId(): number | null {
    return this._selectedPropertyUnitId;
  }

  set selectedPropertyUnitId(value: number | null) {
    this._selectedPropertyUnitId = value;
  }

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly matDialog: MatDialog,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly propertyTenantsService: PropertyTenantsService,
    private readonly accountService: AccountService,
    private readonly store: Store,
  ) {}

  ngOnInit(): void {
    this.activatedRoute.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: Data) => {
      this.data = data;
      this.property = this.propertyTenantsService.property;
      this.fieldDefs = this.store.selectSnapshot(
        ObjectsManagerState.getFieldsByObjectName(RelatedToType.PropertyUnit, {
          allowedFieldsOnly: true,
          addNestedFields: false,
        }),
      );
    });

    this.propertyTenantsService.propertyUnits$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((propertyUnits: PropertyUnit[]) => {
        if (propertyUnits) {
          this.propertyUnits = propertyUnits;

          if (this.propertyUnits.length > 0 && this.selectedPropertyUnit === undefined) {
            this.router.navigate(['./', this.propertyUnits[0].id], { relativeTo: this.activatedRoute });
          }
        }
        this.changeDetectorRef.markForCheck();
      });

    this.propertyTenantsService.propertyUnit$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((propertyUnit: PropertyUnit) => {
        if (propertyUnit) {
          this.selectedPropertyUnit = propertyUnit;
          this.selectedPropertyUnitId = this.selectedPropertyUnit?.id ?? null;
        }
        this.changeDetectorRef.markForCheck();
      });

    this.fetchSavedViews();
  }

  private fetchSavedViews(): void {
    this.accountService.savedViews$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((savedViews: SavedView[]) => {
      if (savedViews) {
        this.savedViews = savedViews.filter((savedView) => savedView.related_to_type === RelatedToType.PropertyUnit);
        this.savedView = this.savedViews[0];
        this.dashboardDef = JSON.parse(this.savedView.definition).dashboard;
        this.panels = JSON.parse(this.savedView.definition).dashboard.panels;
        this.panelDef = this.panels[0][0];
        this.originalFields = clone(this.panelDef.meta.fields);
        this.fields = this.originalFields;
      }
      this.changeDetectorRef.markForCheck();
    });
  }

  private createFieldList(): void {
    this.fieldsCombined = [];
    const leftCol = [];

    this.fields.map((field) => {
      leftCol.push(field[0]);
    });
    this.fieldsCombined.push(leftCol);
  }

  private save(): void {
    for (let col in this.panels) {
      for (let row in this.panels[col]) {
        let panel = this.panels[col][row];
        if (panel.id == this.panelDef.id) {
          this.panels[col][row] = this.panelDef;
        }
      }
    }

    this.dashboardDef.panels = this.panels;
    this.savedView.definition = JSON.stringify({ dashboard: this.dashboardDef });
    this.accountService.updateSavedView(this.savedView).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(noop);
  }

  public addUnit(): void {
    this.matDialog
      .open(PropertyTenantsAddUnitComponent, {
        panelClass: 'mailbox-compose-dialog',
        data: { property: this.property },
      })
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((result) => !!result),
      )
      .subscribe((result: PropertyUnit) => {
        this.router.navigate(['./', result.id as number], { relativeTo: this.activatedRoute });
      });
  }

  public onSelectionChange(event: MatSelectChange): void {
    this.selectedPropertyUnit = this.propertyUnits.find((unit) => unit.id === event.value);
    this.selectedPropertyUnitId = this.selectedPropertyUnit?.id ?? null;
    this.router.navigate(['./', event.value as number], { relativeTo: this.activatedRoute });
  }

  public lookupField(name: string): FieldDef | null {
    let fields = this.fieldDefs.filter((fieldDef) => fieldDef.name == name);
    if (fields.length > 0) {
      return fields[0];
    } else {
      return null;
    }
  }

  public toggleEditMode(editMode: boolean | null = null): void {
    if (editMode === null) {
      this.editMode = !this.editMode;
    } else {
      this.editMode = editMode;
    }
    this.changeDetectorRef.markForCheck();
  }

  public cancelEdits(): void {
    this.fields = this.originalFields;

    if (this.isLayoutChange) {
      this.changeLayout();
    }

    this.panelDef.meta.fields = clone(this.originalFields);

    this.toggleEditMode(false);
  }

  public changeLayout(): void {
    this.isLayoutChange = !this.isLayoutChange;
    this.fields = this.originalFields;
    if (this.isLayoutChange) {
      this.createFieldList();
    }
    this.changeDetectorRef.markForCheck();
  }

  public removeField(row: number, col: number): void {
    this.fields[row].splice(col, 1);
    if (isEmpty(this.fields[row])) {
      this.fields.splice(row, 1);
    }
    this.changeDetectorRef.markForCheck();
  }

  public addRow(): void {
    this.fields.push(['unit_number']);
    this.panelDef.meta.fields.push(['unit_number']);
    this.changeDetectorRef.markForCheck();
  }

  public dropped(event: CdkDragDrop<any[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }
    this.changeDetectorRef.markForCheck();
  }

  public update(params: any): void {
    let currentKey = Object.keys(params)[0];
    let newUnitData = { ...this.selectedPropertyUnit, ...params };
    if (currentKey === 'panel_name') {
      this.panelName = params.panel_name;
    } else {
      const selectColumn = this.defaultFieldDefs.map((m) => m.name) || [];
      selectColumn.push(currentKey);
      this.propertyTenantsService
        .updatePropertyUnit(newUnitData)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          const index = this.propertyUnits.findIndex((item) => item.id === response.id);
          this.propertyUnits[index] = { ...newUnitData };
          this.selectedPropertyUnit = { ...newUnitData };
          this.selectedPropertyUnitId = this.selectedPropertyUnit?.id ?? null;
        });
    }
  }

  public saveLayout(): void {
    if (this.isLayoutChange) {
      this.panelDef.meta.fields = zip(this.fieldsCombined[0], this.fieldsCombined[1]);
      this.originalFields = clone(this.panelDef.meta.fields);
      this.save();
      this.changeLayout();
    } else {
      this.panelDef.meta.fields = this.fields.filter((row) => row && row.length > 0);
      if (this.panelName) {
        this.panelDef.title = this.panelName;
      }
      this.save();
      this.toggleEditMode(false);
    }
  }

  public onFieldSelectionChange(event: any, row: number, col: number): void {
    this.fields[row][col] = event?.value || event?.id || event[this.fields[row][col]];
  }

  public unitName(unit: PropertyUnit): string {
    let latestLease: PropertyTenant;
    if (unit.property_tenants.length > 0) {
      latestLease = unit.property_tenants[0];

      if (latestLease.name === '') return unit.unit_number;

      if (unit.property_tenants.length > 0) {
        unit.property_tenants.forEach((lease) => {
          if (moment(lease.lease_start_date).isAfter(latestLease.lease_start_date)) latestLease = lease;
        });
      }

      return `${unit.unit_number}${latestLease.name ? ' - ' + latestLease.name : ''}`;
    } else {
      return unit.unit_number;
    }
  }

  public canEdit(): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.LEASES_EDIT));
    }

    return true;
  }
}
